<template>
  <div class="FaqContentBlock">
    <div class="grid">
      <div class="col-8-12 push-2-12 sm-push-0 sm-col-1-1">
        <UIHeadingType
          class="mb-xxlg"
          :heading-type="content.seoHeadingtype"
          :title="content.title"
        />
      </div>
      <div class="faq-wrapper col-8-12 push-2-12 sm-push-0 sm-col-1-1">
        <div
          v-if="showFilters"
          class="filters flex col-1-1 no-p mb-md"
        >
          <button
            v-for="(filter, idx) in filtersForUsers"
            :key="filter.id"
            class="filter-button"
            :class="{ active: isFilterActive(0, filter.id) }"
            @click="updateFilterValueView(0, filter.id)"
          >
            {{ filter.label }}
          </button>
        </div>
        <div class="faq-list flex flex-column col-1-1 no-p">
          <UIFaqItem
            v-for="(item, index) in articles"
            :id="(item as any)._id"
            :key="`faq-item-${index}`"
            :title="(item as any).content.question"
            :text="(item as any).content.answerText"
            :is-open="openItemId === (item as any)._id"
            @toggle="handleFaqToggle((item as any)._id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { FAQ_PAGE_ID } from '~/config/elocusConfig'
  import type { FaqContent } from '@/types/contentblocks'
  import type { ElocusPage, BreadCrumb } from '@tc8l/nuxt-base'

  const props = defineProps<{
    content: FaqContent
    breadCrumb?: BreadCrumb[]
    settings?: ElocusPage
    contentblockIdx?: number
  }>()

  const filterData = ref([])

  const { data } = await useBaseAsyncData<ElocusPage>(`/pages/${FAQ_PAGE_ID}/filters`)
  filterData.value = data.value?.filters || []

  const preselectedArticles = ref(props.content.questionList?.map((item) => item) || [])
  const selectedFilters = ref(props.content.categoryIds?.map((item) => item._id) || [])

  const presetFilterIds = computed(() => {
    if (preselectedArticles.value.length > 0) {
      return []
    }
    if (selectedFilters.value.length > 0) {
      return props.content.showFilters == 'true' ? [selectedFilters.value[0]] : selectedFilters.value
    }
    const categoryFilter = filterData.value.find((filter: any) => filter.value === 1264) // Get category filter
    const firstOption = categoryFilter ? categoryFilter.options[0] : null // Get the first option
    return firstOption ? [firstOption.id] : []
  })

  const articleConfig = computed(() => ({
    articles: {
      presetArticles: preselectedArticles.value,
      fillWithPageId: preselectedArticles.value.length === 0,
    },
    seo: {
      changeRoute: false,
      changeHead: false,
    },
    filters: {
      enabled: true,
      show: false,
      multiple: false,
      showResetFilters: false,
      presetSelectedFilterIds: presetFilterIds.value,
    },
    search: {
      enabled: false,
      show: false,
    },
    pagination: {
      enabled: false,
      show: false,
      articlesPerPage: 100,
    },
  }))

  const { filters, updateFilterValue, articles, executeArticles } = useFilterPage(FAQ_PAGE_ID, {
    ...articleConfig.value,
    filterData: filterData.value,
    instanceKey: props.contentblockIdx,
  })

  async function updateFilterValueView(index: number, value: number) {
    updateFilterValue(index, value)
    await executeArticles()
  }

  await executeArticles()

  const openItemId = ref<string | null>(null)

  function handleFaqToggle(itemId: string) {
    openItemId.value = openItemId.value === itemId ? null : itemId
  }

  // These are only the filters which are shown to the users, based on the selected filters or all available filters. Orders them by the categoryIds defined in the contentblock.
  const filtersForUsers = computed(() => {
    if (selectedFilters.value.length === 0) {
      return filterData.value.flatMap((filter) => filter.options)
    }

    const categoryIdOrder = props.content.categoryIds.map((cat) => cat._id)
    const allOptions = filterData.value.flatMap((filter: any) => filter.options)

    return categoryIdOrder.map((categoryId) => allOptions.find((option) => option.id === categoryId)).filter(Boolean)
  })

  const showFilters = computed(
    () =>
      props.content.showFilters == 'true' && filtersForUsers.value.length > 1 && preselectedArticles.value.length === 0,
  )

  const isFilterActive = computed(() => {
    return (filterIdx: number, optionId: number) => {
      const activeFilter = filters.value[filterIdx]
      if (!activeFilter) return false

      if (Array.isArray(activeFilter.values)) {
        return activeFilter.values.includes(optionId)
      }

      return false
    }
  })
</script>

<style lang="scss" scoped>
  .filter-button {
    padding: 0 0 1rem 0;
    border-bottom: 2px solid transparent;
    transition:
      border-color 0.2s,
      color 0.2s,
      font-weight 0.2s;
    font-weight: 400;
    margin-right: $gutter;

    &.active {
      border-bottom: 2px solid $color-text;
      color: $color-text;
      font-weight: 600;
    }
  }
</style>
